import React, { useState, useEffect, useRef } from 'react';
import './App.css';

/* APPROVAL DAY: paste the live Snowflake Marketplace listing URL here,
   then npm run build + deploy. Empty string = "Coming Very Soon" card. */
const SNOWFLAKE_LISTING_URL = 'https://app.snowflake.com/marketplace/listing/GZT1Z3MJIMS'; // LIVE 2026-09-26

/* ── Data ─────────────────────────────────────────────── */
const FEATURES = [
  { icon: '📋', title: 'Requirements Management',  desc: 'AI reviews requirements for gaps and ambiguity before a test is written. Every requirement links directly to sprint tasks and defects for full traceability.' },
  { icon: '🏃', title: 'Sprint & Project Management', desc: 'Plan sprints, assign tasks, track velocity and capacity — all calculated automatically. Kanban board visible to everyone, updated in real time.' },
  { icon: '🤖', title: 'No-Code Test Automation',  desc: 'Build and run test journeys visually — zero scripting required. Handles MFA, CI/CD triggers, and scheduling out of the box.' },
  { icon: '✨', title: 'AI Test Generation',        desc: 'Generate complete test journeys from requirements with one click. AI writes the steps, scores coverage, and detects edge cases you might miss.' },
  { icon: '🔧', title: 'Self-Healing Locators',    desc: 'If a UI element moves after a code change, the bot finds it automatically. No manual script fixes, no flaky tests from refactoring.' },
  { icon: '📊', title: 'Executive Dashboard',      desc: 'Real-time KPIs, quality trends, and risk indicators across all projects. AI Scorecard benchmarks team and individual quality for data-driven conversations.' },
  { icon: '🛡️', title: 'COB & Change Testing',     desc: 'Validate change windows and cutover procedures. Plan and execute business continuity with automated runbooks and disaster recovery workflows.' },
  { icon: '🔍', title: 'Application Discovery',    desc: 'Auto-detect your tech stack, ownership, and vulnerabilities. Always know what you\'re running and who\'s responsible for it.' },
];

const STEPS = [
  { num: '01', title: 'Book a Discovery Call',   desc: '30-minute call to understand your current toolchain, team structure, and delivery goals.' },
  { num: '02', title: 'Live Teams Demo',          desc: 'A full walkthrough inside your own Microsoft Teams environment — your projects, your data.' },
  { num: '03', title: '2-Week Pilot',             desc: 'Your team, your projects, real results in two weeks. No infrastructure setup, no procurement delays.' },
  { num: '04', title: 'Phased Rollout',           desc: 'Structured deployment with role-based onboarding for PMs, BAs, QA engineers, developers, and leadership.' },
];

const COMPARE = [
  { feature: 'Teams-native (tab + bot)',    ip: true,  jira: false,   azure: 'partial', selenium: false },
  { feature: 'No-code automation',          ip: true,  jira: false,   azure: false,     selenium: false },
  { feature: 'AI test generation',          ip: true,  jira: false,   azure: false,     selenium: false },
  { feature: 'Full lifecycle traceability', ip: true,  jira: 'partial', azure: 'partial', selenium: false },
  { feature: 'AI Scorecard & coaching',     ip: true,  jira: false,   azure: false,     selenium: false },
  { feature: 'COB / change window testing', ip: true,  jira: false,   azure: false,     selenium: false },
  { feature: 'Executive dashboard',         ip: true,  jira: 'addon', azure: 'limited', selenium: false },
];

/* ── InView hook ──────────────────────────────────────── */
function useInView(threshold = 0.1) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) setInView(true); },
      { threshold }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, inView];
}

/* ── Navbar ───────────────────────────────────────────── */
function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', fn);
    return () => window.removeEventListener('scroll', fn);
  }, []);
  return (
    <nav className={`navbar ${scrolled ? 'scrolled' : ''}`}>
      <div className="nav-inner">
        <a href="#hero" className="nav-logo">
          <img src="/logos/TechManSys_Logo_Dark.png" alt="Techmansys" />
        </a>
        <div className={`nav-links ${open ? 'open' : ''}`}>
          <a href="#about"    onClick={() => setOpen(false)}>About</a>
          <a href="#services" onClick={() => setOpen(false)}>Services</a>
          <a href="#features" onClick={() => setOpen(false)}>iPerfeQT</a>
          <a href="#editions" onClick={() => setOpen(false)}>Editions</a>
          <a href="#how"      onClick={() => setOpen(false)}>Get Started</a>
          <a href="#contact"  className="nav-cta" onClick={() => setOpen(false)}>Book a Demo</a>
        </div>
        <button className="hamburger" onClick={() => setOpen(p => !p)} aria-label="Toggle menu">
          <span /><span /><span />
        </button>
      </div>
    </nav>
  );
}

/* ── Hero ─────────────────────────────────────────────── */
function Hero() {
  return (
    <section id="hero" className="hero">
      <div className="hero-bg"   aria-hidden="true" />
      <div className="hero-grid" aria-hidden="true" />
      <div className="hero-badge">
        <span className="badge-dot" />
        by Techmansys
      </div>
      <img src="/logos/iPerfeQT.png" alt="iPerfeQT" className="hero-product-logo" />
      <h1 className="hero-heading">
        Stop juggling tools.<br />
        <span className="c-cyan">AI-powered</span> software delivery,<br />
        natively inside <span className="c-blue">Microsoft Teams.</span>
      </h1>
      <p className="hero-sub">
        One platform. Every role. Full lifecycle — from requirements to release —
        with AI that actually does the work. Zero compromises on quality.
      </p>
      <div className="hero-actions">
        <a href="#contact"  className="btn-primary">Book a Free Demo →</a>
        <a href="#features" className="btn-ghost">See All Features</a>
        {SNOWFLAKE_LISTING_URL && (
          <a href={SNOWFLAKE_LISTING_URL} target="_blank" rel="noopener noreferrer" className="btn-ghost">
            ❄ Get it on Snowflake Marketplace →
          </a>
        )}
      </div>
      <div className="hero-proof">
        <div className="proof-item"><span className="proof-val">↓40%</span><span className="proof-lbl">manual testing time</span></div>
        <div className="proof-sep" />
        <div className="proof-item"><span className="proof-val">0 code</span><span className="proof-lbl">to build automation</span></div>
        <div className="proof-sep" />
        <div className="proof-item"><span className="proof-val">30</span><span className="proof-lbl">test journeys in 2 min</span></div>
        <div className="proof-sep" />
        <div className="proof-item"><span className="proof-val">1</span><span className="proof-lbl">platform, 5–6 tools replaced</span></div>
      </div>
    </section>
  );
}


/* ── About ───────────────────────────────────────────────── */
function About() {
  const [ref, inView] = useInView();
  return (
    <section id="about" className="about-section">
      <div className={`about-inner ${inView ? 'visible' : ''}`} ref={ref}>
        <div className="about-left">
          <div className="section-label"><span>{"// Who We Are"}</span></div>
          <h2 className="section-title">
            Built by engineers.<br />
            Driven by <span className="c-cyan">outcomes.</span>
          </h2>
          <p>Techmansys is a technology company focused on helping organisations take control of how they manage and deliver technology. We build tools that reduce complexity, eliminate tool sprawl, and give every stakeholder — from developers to executives — the visibility they need.</p>
          <p style={{ marginTop: '1rem' }}>Our flagship product, iPerfeQT, brings AI-powered software delivery natively into Microsoft Teams — replacing 5–6 disconnected tools with one unified platform built for every role.</p>
          <a href="#services" className="btn-primary" style={{ marginTop: '2rem', display: 'inline-block' }}>Our Services →</a>
        </div>
        <div className="about-right">
          <div className="about-card">
            <img src="/logos/TechManSys_Logo_Dark.png" alt="Techmansys" className="about-card-logo" />
            <div className="about-stats">
              <div className="about-stat">
                <span className="about-stat-val">DE</span>
                <span className="about-stat-lbl">Incorporated in Wilmington, Delaware</span>
              </div>
              <div className="about-stat">
                <span className="about-stat-val">2</span>
                <span className="about-stat-lbl">Products — iPerfeQT Teams & Snowflake</span>
              </div>
              <div className="about-stat">
                <span className="about-stat-val">AI</span>
                <span className="about-stat-lbl">Powered by Cortex AI & Microsoft AI</span>
              </div>
              <div className="about-stat">
                <span className="about-stat-val">🌍</span>
                <span className="about-stat-lbl">Serving clients globally</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ── Professional Services ───────────────────────────────── */
function Services() {
  const [ref, inView] = useInView();
  const services = [
    { icon: '🔍', title: 'Technology Assessment',         desc: 'We assess your current toolchain, identify gaps, and design the right delivery operating model for your organisation.' },
    { icon: '⚙️', title: 'QA Automation Transformation',  desc: 'End-to-end automation programme — strategy through execution. We build, train, and hand over a working automation practice.' },
    { icon: '🔗', title: 'Implementation & Integration',   desc: 'Full iPerfeQT deployment including Microsoft Entra SSO, Teams configuration, CI/CD pipeline integration, and data migration.' },
    { icon: '🎓', title: 'Onboarding & Enablement',        desc: 'Role-based training for PMs, BAs, QA engineers, developers, and leadership. Customised to your environment and ways of working.' },
  ];
  return (
    <section id="services" className="services-section">
      <div className="services-inner">
        <div className="section-label"><span>{"// Professional Services"}</span></div>
        <h2 className="section-title">We don't just sell software.<br /><span className="c-blue">We make it work for you.</span></h2>
        <p className="section-sub">Techmansys offers hands-on consulting and delivery services to ensure your iPerfeQT implementation succeeds from day one.</p>
        <div className={`services-grid ${inView ? 'visible' : ''}`} ref={ref}>
          {services.map((s, i) => (
            <div className="service-card" key={i} style={{ animationDelay: `${i * 0.08}s` }}>
              <div className="service-icon">{s.icon}</div>
              <h3>{s.title}</h3>
              <p>{s.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ── Problem ──────────────────────────────────────────── */
function Problem() {
  const [ref, inView] = useInView();
  const tools = ['Jira / Azure DevOps', 'Zephyr / Xray', 'Selenium / Cypress', 'Confluence / Word', 'Power BI / Excel', 'Email / Teams'];
  return (
    <section className="problem-section">
      <div className={`problem-inner ${inView ? 'visible' : ''}`} ref={ref}>
        <div className="problem-left">
          <div className="section-label"><span>{"// The Problem"}</span></div>
          <h2 className="section-title">Your team uses <span className="c-cyan">5–6 disconnected tools.</span></h2>
          <p className="section-sub">Context switching, data silos, missed coverage, poor adoption — and nobody has the full picture. iPerfeQT fixes this.</p>
        </div>
        <div className="problem-tools">
          {tools.map((t, i) => (
            <div className="problem-tool" key={i} style={{ animationDelay: `${i * 0.07}s` }}>
              <span className="tool-x">✕</span> {t}
            </div>
          ))}
          <div className="problem-tool replace" style={{ animationDelay: '0.5s' }}>
            <img src="/logos/iPerfeQT.png" alt="iPerfeQT" style={{ height: 18 }} /> iPerfeQT — one platform
          </div>
        </div>
      </div>
    </section>
  );
}

/* ── Features ─────────────────────────────────────────── */
function Features() {
  const [ref, inView] = useInView();
  return (
    <section id="features" className="features-section">
      <div className="features-inner">
        <div className="section-label"><span>{"// Full Feature Set"}</span></div>
        <h2 className="section-title">Everything in <span className="c-blue">one platform.</span></h2>
        <p className="section-sub">iPerfeQT covers the complete software delivery lifecycle — AI-powered, Teams-native, and built for every role.</p>
        <div className={`features-grid ${inView ? 'visible' : ''}`} ref={ref}>
          {FEATURES.map((f, i) => (
            <div className="feature-card" key={i} style={{ animationDelay: `${i * 0.06}s` }}>
              <div className="feature-icon">{f.icon}</div>
              <h3>{f.title}</h3>
              <p>{f.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ── Editions ─────────────────────────────────────────── */
function Editions() {
  const [ref, inView] = useInView();
  return (
    <section id="editions" className="editions-section">
      <div className="editions-inner">
        <div className="section-label"><span>{"// Two Editions"}</span></div>
        <h2 className="section-title">Pick your <span className="c-cyan">platform.</span></h2>
        <p className="section-sub">iPerfeQT comes in two flavours — one for Microsoft Teams today, and one for Snowflake very soon.</p>
        <div className={`flavors-grid ${inView ? 'visible' : ''}`} ref={ref}>
          <div className="flavor-card teams" style={{ animationDelay: '0s' }}>
            <div className="flavor-tag avail">✓ Available Now</div>
            <h3>iPerfeQT for Microsoft Teams</h3>
            <p>Your team is already in Teams every day. iPerfeQT meets them there — no new browser tabs, no new logins, no adoption battle. Sign in once with your Microsoft credentials and run your entire delivery lifecycle from the sidebar.</p>
            <ul className="flavor-features">
              <li>Teams-native sidebar tab — no context switching</li>
              <li>Microsoft SSO — one login, no separate passwords</li>
              <li>AI chatbot — "show my open defects" just works</li>
              <li>No-code automation with self-healing locators</li>
              <li>AI test generation from requirements in one click</li>
              <li>Full traceability: requirement → sprint → defect → release</li>
              <li>Executive dashboard with live KPIs and AI Scorecard</li>
              <li>CI/CD integration — fail the pipeline before production</li>
            </ul>
            <a href="#contact" className="btn-primary flavor-btn">Book a Demo →</a>
            <div className="flavor-glow" />
          </div>
          <div className="flavor-card snow" style={{ animationDelay: '0.1s' }}>
            <div className="flavor-tag soon">{SNOWFLAKE_LISTING_URL ? '● Live on Snowflake Marketplace' : '⬡ Coming Very Soon'}</div>
            <h3>iPerfeQT for Snowflake</h3>
            <p>Audit your Snowflake data quality &amp; compliance without your data ever leaving your account. iPerfeQT installs as a Snowflake Native App and runs 60+ security, governance, data-quality and cost audits in-account — zero data movement, no shared credentials. Cortex AI turns findings into remediation guidance and drafts audit-ready compliance reports.</p>
            <ul className="flavor-features">
              <li>Snowflake Native App — runs inside your account, zero data egress</li>
              <li>60+ audits across security, governance, data quality &amp; cost</li>
              <li>PII Native Scanner — Cortex classifies sensitive columns in place</li>
              <li>AI Compliance Officer — SOC 2 / GDPR / DPDP report drafts</li>
              <li>Cortex AI remediation guidance for every finding</li>
              <li>Six-role review chain with audit-ready evidence</li>
              <li>{SNOWFLAKE_LISTING_URL
                ? <a href={SNOWFLAKE_LISTING_URL} target="_blank" rel="noopener noreferrer">Get it on the Snowflake Marketplace →</a>
                : 'Available on the Snowflake Marketplace'}</li>
            </ul>
            {SNOWFLAKE_LISTING_URL
              ? <a href={SNOWFLAKE_LISTING_URL} target="_blank" rel="noopener noreferrer" className="btn-primary flavor-btn">Get it on Snowflake Marketplace →</a>
              : <a href="#contact" className="btn-primary flavor-btn">Request a Demo →</a>}
            <div className="flavor-glow" />
          </div>
        </div>
      </div>
    </section>
  );
}

/* ── Compare ──────────────────────────────────────────── */
function Compare() {
  const [ref, inView] = useInView();
  const cell = (v) => {
    if (v === true)      return <span className="cmp-yes">✓</span>;
    if (v === false)     return <span className="cmp-no">✕</span>;
    return <span className="cmp-partial">{v}</span>;
  };
  return (
    <section id="compare" className="compare-section">
      <div className={`compare-inner ${inView ? 'visible' : ''}`} ref={ref}>
        <div className="section-label"><span>{"// How We Compare"}</span></div>
        <h2 className="section-title">What only <span className="c-cyan">iPerfeQT</span> delivers.</h2>
        <div className="compare-table-wrap">
          <table className="compare-table">
            <thead>
              <tr>
                <th>Feature</th>
                <th><img src="/logos/iPerfeQT.png" alt="iPerfeQT" style={{ height: 36, width: "auto" }} /></th>
                <th>Jira + Zephyr</th>
                <th>Azure Test Plans</th>
                <th>Selenium / Cypress</th>
              </tr>
            </thead>
            <tbody>
              {COMPARE.map((row, i) => (
                <tr key={i}>
                  <td>{row.feature}</td>
                  <td>{cell(row.ip)}</td>
                  <td>{cell(row.jira)}</td>
                  <td>{cell(row.azure)}</td>
                  <td>{cell(row.selenium)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}

/* ── How It Works ─────────────────────────────────────── */
function HowItWorks() {
  const [ref, inView] = useInView();
  return (
    <section id="how" className="how-section">
      <div className="how-inner">
        <div className="section-label"><span>{"// Get Started"}</span></div>
        <h2 className="section-title">Up and running <span className="c-cyan">in two weeks.</span></h2>
        <p className="section-sub">No infrastructure setup. No long procurement. Start with a free 2-week pilot inside your Microsoft Teams environment.</p>
        <div className={`steps ${inView ? 'visible' : ''}`} ref={ref}>
          {STEPS.map((s, i) => (
            <div className="step-card" key={i} style={{ animationDelay: `${i * 0.1}s` }}>
              <div className="step-num">{s.num}</div>
              <h3>{s.title}</h3>
              <p>{s.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ── CTA ──────────────────────────────────────────────── */
function CTA() {
  return (
    <section id="contact" className="cta-section">
      <div className="cta-inner">
        <img src="/logos/iPerfeQT.png" alt="iPerfeQT" className="cta-logo" />
        <h2>Ready to see it in action?</h2>
        <p>Start with a 2-week pilot inside your Microsoft Teams environment. Your team, your projects, real results.</p>
        <div className="cta-actions">
          <a href="mailto:contact@techmansys.com" className="btn-primary">Book a Demo →</a>
          <a href="https://iperfeqt.com" className="btn-ghost" target="_blank" rel="noopener noreferrer">Visit iperfeqt.com</a>
        </div>
        <p className="cta-contact">
          📍 3911 Concord Pike #8030, SMB#46779, Wilmington, DE 19803 &nbsp;·&nbsp;
          <a href="mailto:contact@techmansys.com">contact@techmansys.com</a> &nbsp;·&nbsp;
          +1 (470) 844-1260
        </p>
      </div>
    </section>
  );
}

/* ── Footer ───────────────────────────────────────────── */
function Footer() {
  return (
    <footer className="footer">
      <div className="footer-inner">
        <div className="footer-logos">
          <img src="/logos/TechManSys_Logo_Dark.png" alt="Techmansys" className="footer-tms" />
          <span className="footer-dot">·</span>
          <img src="/logos/iPerfeQT.png" alt="iPerfeQT" className="footer-ip" />
        </div>
        <div className="footer-links">
          <a href="#about">About</a>
          <a href="#services">Services</a>
          <a href="#features">iPerfeQT</a>
          <a href="#editions">Editions</a>
          <a href="#how">Get Started</a>
          <a href="#contact">Contact</a>
          <a href="/privacy.html">Privacy</a>
          <a href="/terms.html">Terms</a>
          <a href="/legal.html">Legal</a>
        </div>
        <div className="footer-copy">© {new Date().getFullYear()} Techmansys Inc. All rights reserved.</div>
      </div>
    </footer>
  );
}

/* ── App ──────────────────────────────────────────────── */
export default function App() {
  return (
    <>
      <Navbar />
      <Hero />
      <About />
      <Services />
      <Problem />
      <Features />
      <Editions />
      <Compare />
      <HowItWorks />
      <CTA />
      <Footer />
    </>
  );
}
